import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { filter, Observable, of, ReplaySubject, take, takeUntil } from 'rxjs';
import { AppState } from '../store/app.state';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { PersonActions } from '@states/person/person.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsModel } from '@models/person-groups.model';
import { GroupNameDialogComponent } from '../pages/database/pages/person/dialogs/group-name-dialog/group-name-dialog.component';
import { JoinGroupDialogComponent } from '../pages/database/pages/person/dialogs/join-group-dialog/join-group-dialog.component';
import { ShareGroupDialogComponent } from '../pages/database/pages/person/dialogs/share-group-dialog/share-group-dialog.component';
import { AddPersonToGroupDialogComponent } from '../pages/database/pages/person/dialogs/add-person-to-group-dialog/add-person-to-group-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class PersonGroupsDialogService {
  private isNameDialogOpen = false;
  private joinDialogRef: MatDialogRef<JoinGroupDialogComponent> | null = null;

  public constructor(
    private readonly dialog: MatDialog,
    private readonly store$: Store<AppState>,
    private readonly actions$: Actions,
  ) {}

  public openCreate(): Observable<PersonGroupsModel.PersonGroupListDocument | null> {
    if (this.isNameDialogOpen) {
      return of(null);
    }
    this.isNameDialogOpen = true;
    const createdGroup$ = new ReplaySubject<PersonGroupsModel.PersonGroupListDocument | null>(1);
    let createdGroupEmitted = false;
    let createInProgress = false;

    const dialogRef = this.dialog.open(GroupNameDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
        if (!createdGroupEmitted) {
          createdGroup$.next(null);
          createdGroup$.complete();
        }
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((name: string) => {
        if (createInProgress) {
          return;
        }

        this.store$.select(personGroupsFeature.selectAllPersonGroups).pipe(take(1)).subscribe((all) => {
          if (PersonGroupsModel.isDuplicatePersonGroupName(name, all)) {
            dialogRef.componentInstance.setDuplicateError(name);
            return;
          }
          const normalizedName = name.trim().toLowerCase();
          createInProgress = true;

          this.actions$
            .pipe(
              ofType(PersonGroupsActions.createPersonGroupSuccess),
              filter(({ personGroup }) => personGroup.name.trim().toLowerCase() === normalizedName),
              take(1),
              takeUntil(dialogRef.afterClosed()),
            )
            .subscribe(({ personGroup }) => {
              createdGroupEmitted = true;
              createdGroup$.next(personGroup);
              createdGroup$.complete();
              dialogRef.close();
            });

          this.actions$
            .pipe(
              ofType(PersonGroupsActions.createPersonGroupFail),
              take(1),
              takeUntil(dialogRef.afterClosed()),
            )
            .subscribe(() => {
              createInProgress = false;
            });

          this.store$.dispatch(PersonGroupsActions.createPersonGroup({ name }));
        });
      });

    return createdGroup$.asObservable();
  }

  public openShare(groupId: string, groupName: string, createdByOrgId: string): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;

    const data: PersonGroupsModel.ShareGroupDialogData = {
      groupId,
      groupName,
      createdByOrgId,
    };

    const dialogRef = this.dialog.open(ShareGroupDialogComponent, {
      width: '660px',
      height: '80vh',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
        this.store$.dispatch(PersonGroupsActions.clearPersonGroupShareOptions());
      });
  }

  /**
   * Opens join-group dialog. When another person-group modal is already open (non-join), returns null.
   * If the join dialog is already open, it is closed first so a route-driven reopen can apply a new invite code.
   */
  public openJoinDialog(
    routeParams?: Record<string, string>,
  ): MatDialogRef<JoinGroupDialogComponent> | null {
    const raw = routeParams?.[PersonGroupsModel.JoinRouteQueryParam.InviteCode];
    const initialCode =
      raw != null && String(raw).trim() !== '' ? String(raw).trim() : undefined;
    const data: PersonGroupsModel.JoinGroupDialogData | undefined = initialCode
      ? { initialCode }
      : undefined;

    if (this.joinDialogRef) {
      this.joinDialogRef.close();
    }

    this.isNameDialogOpen = true;
    const dialogRef = this.dialog.open(JoinGroupDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });
    this.joinDialogRef = dialogRef;

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.joinDialogRef === dialogRef) {
          this.joinDialogRef = null;
          this.isNameDialogOpen = false;
        }
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((code: string) => {
        this.store$.dispatch(PersonGroupsActions.joinPersonGroup({ code }));
        dialogRef.close();
      });

    return dialogRef;
  }

  public openAddPersonsToGroup(
    groupId: string,
    groupName: string,
    personGroupMemberIds: number[],
    ): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;
    this.store$.dispatch(PersonActions.getSavedPersons());
    this.store$.dispatch(PersonActions.getUnsavedPersons());

    const data: PersonGroupsModel.AddPersonToGroupDialogData = {
      groupId,
      groupName,
      personGroupMemberIds
    };

    const dialogRef = this.dialog.open(AddPersonToGroupDialogComponent, {
      width: '800px',
      height: '80vh',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
      });
  }

  public openEdit(groupId: string, currentName: string): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;

    const data: PersonGroupsModel.GroupNameDialogData = {
      initialName: currentName,
      titleKey: 'PERSON_GROUPS.NAME_DIALOG.EDIT_TITLE',
      submitKey: 'COMMON.SAVE',
    };

    const dialogRef = this.dialog.open(GroupNameDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((name: string) => {
        this.store$.select(personGroupsFeature.selectAllPersonGroups).pipe(take(1)).subscribe((all) => {
          if (PersonGroupsModel.isDuplicatePersonGroupName(name, all, groupId)) {
            dialogRef.componentInstance.setDuplicateError(name);
            return;
          }
          this.store$.dispatch(PersonGroupsActions.updatePersonGroup({ id: groupId, name }));
          dialogRef.close();
        });
      });
  }
}
